'use client';

import React, { useCallback, useRef } from 'react';

import {
  GridCellModes,
  GridCellModesModel,
  GridRowModel,
  MuiEvent,
  GridEventListener,
  GridCellParams,
} from '@mui/x-data-grid';

import { toast } from '../components/display/toaster';

/**
 * This hook is used to handle row clicks on the data grid.
 * It prevents the default behavior of the row click and only triggers the onClick event if the user clicks on the row without dragging.
 */
export const useDataGridClick = ({
  onClick,
  threshold = 10,
}: {
  onClick: (
    record: GridRowModel | GridCellModesModel,
    event: MuiEvent<React.MouseEvent<HTMLElement>>,
  ) => void;
  threshold?: number;
}) => {
  const {
    cellModesModel,
    handleCellClick: handleCellClickSingleClick,
    handleCellModesModelChange,
  } = useSingleClickEditingMode();
  const [mouseDownPos, setMouseDownPos] = React.useState({ x: 0, y: 0 });

  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    setMouseDownPos({ x: event.clientX, y: event.clientY });
  };

  const handleCellClick: GridEventListener<'cellClick'> = useCallback(
    (params, event) => {
      const isCheckboxField = params.field === '__check__';

      if (!isCheckboxField) {
        event.stopPropagation();
        event.preventDefault();
      }

      if (params.isEditable || isCheckboxField) {
        handleCellClickSingleClick(params, event);

        return;
      }

      const dx = Math.abs(event.clientX - mouseDownPos.x);
      const isDragging = dx > threshold;

      if (!isDragging) {
        onClick(params, event);
      }
    },
    [onClick, threshold, mouseDownPos, handleCellClickSingleClick],
  );

  return {
    cellModesModel,
    handleCellClick,
    handleCellModesModelChange,
    handleMouseDown,
  };
};

const useSingleClickEditingMode = () => {
  const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>({});
  const lastToastTimeRef = useRef<number>(0);

  const handleCellClick = React.useCallback((params: GridCellParams, event: React.MouseEvent) => {
    if (!params.isEditable) {
      return;
    }

    if (
      (event.target as any).nodeType === 1 &&
      !event.currentTarget.contains(event.target as Element)
    ) {
      return;
    }

    setCellModesModel((prevModel) => {
      const newModel = {
        ...Object.fromEntries(
          Object.keys(prevModel).map((id) => [
            id,
            Object.fromEntries(
              Object.keys(prevModel[id]).map((field) => [field, { mode: GridCellModes.View }]),
            ),
          ]),
        ),
        [params.id]: {
          ...Object.fromEntries(
            Object.keys(prevModel[params.id] || {}).map((field) => [
              field,
              { mode: GridCellModes.View },
            ]),
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      };

      // if editable show hint toast but only if not recently shown
      if (newModel[params.id]?.[params.field]?.mode === GridCellModes.Edit) {
        const now = Date.now();
        if (now - lastToastTimeRef.current > 30 * 1000) {
          toast.info('Press enter to save the value. Press escape to cancel.');
          lastToastTimeRef.current = now;
        }
      }

      return newModel;
    });
  }, []);

  const handleCellModesModelChange = React.useCallback((newModel: GridCellModesModel) => {
    setCellModesModel(newModel);
  }, []);

  return { cellModesModel, handleCellClick, handleCellModesModelChange };
};
